/**
 * Figure attachment for a question (diagram / image shown under the question text).
 *
 *   <FigureField value={media_url} onChange={(dataUrl|'' ) => …} />
 *
 * Empty state: "Attach figure" (click or drop an image). With a figure: preview + Replace + Remove.
 * Images are resized client-side (max 1200px, PNG kept for line diagrams, photos as JPEG) and stored
 * as a data URL in `media_url`, so a question stays self-contained in every export / import.
 */
import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Image as ImageIcon, ArrowsClockwise, Trash, Paperclip } from '@phosphor-icons/react';
import { toast } from 'sonner';

const MAX_SIDE = 1200;
const MAX_BYTES = 1.5 * 1024 * 1024; // after resize

export async function fileToFigureDataUrl(file) {
  if (!file.type.startsWith('image/')) throw new Error('Please choose an image file (PNG, JPG, GIF, WebP or SVG)');
  if (file.type === 'image/svg+xml' || file.type === 'image/gif') {
    if (file.size > MAX_BYTES) throw new Error('Image is larger than 1.5 MB');
    return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
  }
  const bitmap = await new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = () => rej(new Error('Could not read the image'));
    img.src = URL.createObjectURL(file);
  });
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext('2d');
  if (file.type !== 'image/png') { ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height); }
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  URL.revokeObjectURL(bitmap.src);
  let out = file.type === 'image/png' ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.86);
  if (out.length > MAX_BYTES * 1.37 && file.type === 'image/png') out = canvas.toDataURL('image/jpeg', 0.86);
  if (out.length > MAX_BYTES * 1.37) throw new Error('Image is still larger than 1.5 MB after resizing — please use a smaller picture');
  return out;
}

export default function FigureField({ value, onChange, label = 'Figure', hint, testid = 'figure-field', className = '' }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);

  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      onChange?.(await fileToFigureDataUrl(file));
    } catch (e) {
      toast.error(e.message || 'Could not attach the figure');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`field ${className}`} data-testid={testid}>
      <Label>{label} <span className="text-muted-foreground font-normal">(optional)</span></Label>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} data-testid={`${testid}-input`} />
      {value ? (
        <div className="flex items-start gap-3">
          <img src={value} alt="Question figure" className="max-h-48 max-w-[22rem] rounded border border-border bg-white object-contain" data-testid={`${testid}-preview`} />
          <div className="flex flex-col gap-1.5">
            <Button type="button" variant="outline" size="sm" className="h-8 justify-start" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${testid}-replace`}>
              <ArrowsClockwise className="w-4 h-4" /> Replace figure
            </Button>
            <Button type="button" variant="outline" size="sm" className="h-8 justify-start text-destructive hover:text-destructive" onClick={() => onChange?.('')} data-testid={`${testid}-remove`}>
              <Trash className="w-4 h-4" /> Remove figure
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files?.[0]); }}
          disabled={busy}
          className={`w-full max-w-md flex items-center gap-3 rounded border-2 border-dashed px-3 py-2.5 text-left transition-colors ${dragging ? 'border-shell-primary bg-muted/40' : 'border-border hover:border-shell-primary hover:bg-muted/30'}`}
          data-testid={`${testid}-attach`}
        >
          <span className="w-9 h-9 rounded bg-muted flex items-center justify-center text-muted-foreground flex-shrink-0">
            <ImageIcon className="w-5 h-5" />
          </span>
          <span className="min-w-0">
            <span className="block text-[13px] font-medium flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> {busy ? 'Attaching…' : 'Attach figure'}</span>
            <span className="block field-hint">Click to choose or drop an image — diagram, graph, picture. Shown under the question text.</span>
          </span>
        </button>
      )}
      {hint && <p className="field-hint">{hint}</p>}
    </div>
  );
}
